"use client"

import { useEffect, useRef, useState } from "react"
import "./key-features.css"

/**
 * "Inside Velova" key-features bento — port of the Claude Design export
 * `Key Features Section.dc.html` (Velova Hero Visual v2 Restructure (4)).
 * Sits on the homepage right after the Velova Loop (Method) section.
 *
 * Five product moments in one 12-col bento (Performance, Answer evidence,
 * Questions, Competitors, Sources) plus the bridge line into the Actions
 * story. The export's DCLogic animation is ported below: per-card reveal
 * via IntersectionObserver, then one shared 3.4s clock that cycles the
 * question group — tabs switch, metrics re-settle, the trend redraws,
 * competitor bars re-measure, sources scan and answer mentions light up
 * with their citations. Reduced motion keeps the finished state.
 *
 * Requested deltas from the export: AI-engine marks use the official
 * first-party logos already shipped in /img/ai-surfaces/, and the brands
 * and domains in Competitors/Sources carry real company marks served
 * from /img/kf-brands/ (sized to match the engine chips).
 */

/* The /img/ai-surfaces/ set is the official white-on-dark variant (built for
 * the marquee); these chips sit on WHITE circles, so we use the same official
 * marks in their colored light-background form, shipped with the design
 * export at /search-shift/. */
const ENGINE = {
  chatgpt: "/search-shift/engine-chatgpt.svg",
  gemini: "/search-shift/engine-gemini.svg",
  perplexity: "/search-shift/engine-perplexity.svg",
  claude: "/search-shift/engine-claude.svg",
} as const

const BRAND = {
  apple: "/img/kf-brands/apple.png",
  oura: "/img/kf-brands/ouraring.png",
  whoop: "/img/kf-brands/whoop.png",
  fitbit: "/img/kf-brands/fitbit.png",
  tomsguide: "/img/kf-brands/tomsguide.png",
  reddit: "/img/kf-brands/reddit.png",
} as const

function EngineStack() {
  return (
    <span aria-hidden="true" className="velova-kf-engines">
      <span data-kf="pop" className="velova-kf-engine"><img src={ENGINE.chatgpt} alt="" /></span>
      <span data-kf="pop" className="velova-kf-engine"><img src={ENGINE.gemini} alt="" /></span>
      <span data-kf="pop" className="velova-kf-engine"><img src={ENGINE.perplexity} alt="" /></span>
      <span data-kf="pop" className="velova-kf-engine"><img src={ENGINE.claude} alt="" /></span>
    </span>
  )
}

function BrandMark({ src }: { src: string }) {
  return (
    <span aria-hidden="true" data-kf="pop" className="velova-kf-brandmark">
      <img src={src} alt="" />
    </span>
  )
}

type Tab = "discovery" | "comparison" | "trust"
const TABS: Tab[] = ["discovery", "comparison", "trust"]

export function KeyFeatures() {
  const rootRef = useRef<HTMLElement>(null)
  const qpanelRef = useRef<HTMLDivElement>(null)
  const [tab, setTab] = useState<Tab>("discovery")
  const armedRef = useRef(false)
  // a user tab choice pins the question group for a while — the ambient
  // clock keeps running but respects the selection instead of yanking it
  const holdUntilRef = useRef(0)
  const heldTabRef = useRef<Tab>("discovery")

  const selectTab = (t: Tab) => {
    heldTabRef.current = t
    holdUntilRef.current = Date.now() + 12000
    setTab(t)
  }

  // tab change → staggered panel swap: rows cascade in, meters re-measure,
  // engine chips and verdict marks pop (upgrade of DCLogic.fadePanel)
  useEffect(() => {
    if (!armedRef.current) return
    const p = qpanelRef.current
    if (!p) return
    const timers: number[] = []
    const rows = Array.from(p.querySelectorAll<HTMLElement>(".velova-kf-qrow"))
    const fills = Array.from(p.querySelectorAll<HTMLElement>(".velova-kf-meter-fill"))
    const chips = Array.from(
      p.querySelectorAll<HTMLElement>(".velova-kf-engine-sm, .velova-kf-qmark, .velova-kf-vbadge"),
    )
    rows.forEach((el) =>
      Object.assign(el.style, { transition: "none", opacity: "0", transform: "translateY(8px)" }),
    )
    fills.forEach((el) => Object.assign(el.style, { transition: "none", transform: "scaleX(0)" }))
    chips.forEach((el) =>
      Object.assign(el.style, { transition: "none", opacity: "0", transform: "scale(.6)" }),
    )
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        rows.forEach((el, j) =>
          timers.push(
            window.setTimeout(() => {
              Object.assign(el.style, {
                transition: "opacity .45s ease, transform .45s cubic-bezier(.2,.7,.2,1)",
                opacity: "1",
                transform: "none",
              })
            }, j * 90),
          ),
        )
        chips.forEach((el, j) =>
          timers.push(
            window.setTimeout(() => {
              Object.assign(el.style, {
                transition: "opacity .4s ease, transform .4s cubic-bezier(.2,.9,.3,1)",
                opacity: "1",
                transform: "none",
              })
            }, 200 + j * 70),
          ),
        )
        fills.forEach((el, j) =>
          timers.push(
            window.setTimeout(() => {
              Object.assign(el.style, {
                transition: "transform .7s cubic-bezier(.2,.7,.2,1)",
                transform: "scaleX(1)",
              })
            }, 320 + j * 110),
          ),
        )
      }),
    )
    return () => timers.forEach(clearTimeout)
  }, [tab])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const q = (sel: string) => Array.from(root.querySelectorAll<HTMLElement>(sel))
    const set = (el: HTMLElement | SVGElement | null | undefined, p: Record<string, string>) => {
      if (el) Object.assign(el.style, p)
    }
    const setAll = (list: HTMLElement[], p: Record<string, string>) => list.forEach((el) => set(el, p))

    let timers: number[] = []
    let beatTimers: number[] = []
    let clock: number | null = null
    let beat: number | null = null
    let trendLen = 700
    const revealed: Record<number, boolean> = {}
    const vis: Record<number, boolean> = {}
    const observers: IntersectionObserver[] = []

    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms))
    const beatAt = (ms: number, fn: () => void) => beatTimers.push(window.setTimeout(fn, ms))
    const clearBeat = () => {
      beatTimers.forEach(clearTimeout)
      beatTimers = []
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    // ---- arm: hide the animated parts; the template holds the end state ---
    armedRef.current = true
    setAll(q('[data-kf="hdr"]'), { transition: "none", opacity: "0", transform: "translateY(16px)" })
    q('[data-kf="card"]').forEach((el, i) => {
      el.dataset.kfIndex = String(i)
      set(el, { transition: "none", opacity: "0", transform: "translateY(26px)" })
    })
    setAll(q('[data-kf="metric"]'), { transition: "none", opacity: "0", transform: "translateY(9px)" })
    setAll(q('[data-kf="srow"]'), { transition: "none", opacity: "0", transform: "translateY(7px)" })
    setAll(q('[data-kf="sup"], [data-kf="cite"]'), { transition: "none", opacity: "0" })
    setAll(q('[data-kf="cbar"]'), { transition: "none", transform: "scaleX(0)" })
    set(q('[data-kf="tdot"]')[0], { transition: "none", opacity: "0", transform: "scale(.4)" })
    set(q('[data-kf="trendfill"]')[0], { transition: "none", opacity: "0" })
    // illustrations: engine/brand chips pop in, card footers and the
    // trend's forecast band fade in, the filter bar cascades
    setAll(q('[data-kf="pop"]'), { transition: "none", opacity: "0", transform: "scale(.5)" })
    setAll(q('[data-kf="foot"]'), { transition: "none", opacity: "0", transform: "translateY(6px)" })
    setAll(q('[data-kf="fband"]'), { transition: "none", opacity: "0" })
    setAll(q(".velova-kf-filterbar > *"), { transition: "none", opacity: "0", transform: "translateY(4px)" })
    // brand mentions start as plain text, then light up as Velova detects them
    q('[data-kf="hl"]').forEach((el) =>
      set(el, { transition: "none", borderColor: "transparent", background: "transparent", boxShadow: "none" }),
    )
    const trendLine = root.querySelector<SVGPathElement>('[data-kf="trend"]')
    if (trendLine && trendLine.getTotalLength) {
      trendLen = Math.ceil(trendLine.getTotalLength())
      Object.assign(trendLine.style, {
        transition: "none",
        strokeDasharray: String(trendLen),
        strokeDashoffset: String(trendLen),
      })
    }

    // ---- per-card intros ------------------------------------------------
    const playPerformance = () => {
      q(".velova-kf-filterbar > *").forEach((el, j) =>
        at(140 + j * 80, () =>
          set(el, { transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }),
        ),
      )
      q('[data-kf="metric"]').forEach((el, j) =>
        at(220 + j * 120, () =>
          set(el, { transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }),
        ),
      )
      at(520, () => {
        set(trendLine, { transition: "stroke-dashoffset 1.6s cubic-bezier(.35,0,.25,1)", strokeDashoffset: "0" })
        set(q('[data-kf="trendfill"]')[0], { transition: "opacity 1.4s ease .4s", opacity: "1" })
      })
      // the forecast band arrives just as the line reaches it
      q('[data-kf="fband"]').forEach((el, j) =>
        at(1550 + j * 120, () => set(el, { transition: "opacity .7s ease", opacity: "1" })),
      )
      at(1900, () =>
        set(q('[data-kf="tdot"]')[0], { transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.9,.3,1)", opacity: "1", transform: "none" }),
      )
    }
    const playEvidence = () => {
      q('[data-kf="hl"]').forEach((el, j) =>
        at(420 + j * 240, () => {
          el.style.transition = "border-color .55s ease, background .55s ease, box-shadow .55s ease"
          el.style.borderColor = ""
          el.style.background = ""
          el.style.boxShadow = ""
        }),
      )
      q('[data-kf="sup"]').forEach((el, j) =>
        at(560 + j * 240, () => set(el, { transition: "opacity .45s ease", opacity: "1" })),
      )
      q('[data-kf="cite"]').forEach((el, j) =>
        at(1320 + j * 110, () => set(el, { transition: "opacity .5s ease", opacity: "1" })),
      )
    }
    const playCompetitors = () => {
      q('[data-kf="cbar"]').forEach((el, j) =>
        at(260 + j * 120, () => set(el, { transition: "transform .95s cubic-bezier(.2,.7,.2,1)", transform: "scaleX(1)" })),
      )
    }
    const playSources = () => {
      q('[data-kf="srow"]').forEach((el, j) =>
        at(240 + j * 100, () =>
          set(el, { transition: "opacity .55s ease, transform .55s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }),
        ),
      )
    }
    const playCard = (i: number) => {
      if (i === 0) playPerformance()
      else if (i === 1) playEvidence()
      else if (i === 3) playCompetitors()
      else if (i === 4) playSources()
      // every card: its chips pop in with a slight overshoot, then footers settle
      const card = q('[data-kf="card"]')[i]
      if (!card) return
      Array.from(card.querySelectorAll<HTMLElement>('[data-kf="pop"]')).forEach((el, j) =>
        at(500 + j * 90, () =>
          set(el, {
            transition: "opacity .4s ease, transform .45s cubic-bezier(.2,.9,.3,1)",
            opacity: "1",
            transform: "none",
          }),
        ),
      )
      Array.from(card.querySelectorAll<HTMLElement>('[data-kf="foot"]')).forEach((el) =>
        at(820, () =>
          set(el, { transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }),
        ),
      )
    }

    const reveal = (el: HTMLElement) => {
      at(Number(el.dataset.kfDelay || 0), () => {
        set(el, { transition: "opacity .75s ease, transform .75s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" })
        if (el.dataset.kf === "card") {
          const i = Number(el.dataset.kfIndex)
          revealed[i] = true
          playCard(i)
          // hand transform/transition back to CSS once the intro settles,
          // so the :hover lift in the stylesheet can take over
          at(900, () => set(el, { transition: "", transform: "" }))
        }
      })
    }

    // ---- ambient clock --------------------------------------------------
    const setMetrics = (g: number) => {
      if (!revealed[0]) return
      const SETS: [string, string, string, string, string][][] = [
        [["8.3%", "▼", "4.6", "#D9B98A", "Needs attention"], ["6.0%", "▼", "3.3", "#D9B98A", "Low"], ["3.6%", "▲", "1.1", "#8FD9C4", "Medium"]],
        [["5.1%", "▼", "6.2", "#D9B98A", "Needs attention"], ["4.4%", "▼", "2.8", "#D9B98A", "Low"], ["1.9%", "▼", "2.4", "#D9B98A", "Low"]],
        [["11.7%", "▲", "2.1", "#8FD9C4", "Improving"], ["8.6%", "▲", "1.4", "#8FD9C4", "Steady"], ["5.2%", "▲", "0.8", "#8FD9C4", "Medium"]],
      ]
      const FILTER = [
        ["Discovery questions", "Jun 12 – Jul 11"],
        ["Comparison questions", "Jun 19 – Jul 18"],
        ["Trust questions", "Jun 26 – Jul 25"],
      ]
      ;[q('[data-kf="fgroup"]')[0], q('[data-kf="frange"]')[0]].forEach((el, k) => {
        if (!el) return
        set(el, { opacity: "0" })
        beatAt(240 + k * 80, () => {
          el.textContent = FILTER[g][k]
          set(el, { opacity: "1" })
        })
      })
      const MOVE: [string, string][] = [
        ["Biggest daily move ▼5.7%", "#D9B98A"],
        ["Biggest daily move ▼7.4%", "#D9B98A"],
        ["Biggest daily move ▲2.9%", "#8FD9C4"],
      ]
      const big = q('[data-kf="bigmove"]')[0]
      if (big) {
        set(big, { opacity: "0" })
        beatAt(300, () => {
          big.textContent = MOVE[g][0]
          set(big, { color: MOVE[g][1], opacity: "1" })
        })
      }
      const vals = q('[data-kf="mval"]')
      const deltas = q('[data-kf="mdelta"]')
      const notes = q('[data-kf="mnote"]')
      SETS[g].forEach((row, j) =>
        beatAt(j * 110, () => {
          const parts = [vals[j], deltas[j], notes[j]]
          setAll(parts, { transition: "opacity .26s ease, transform .26s ease", opacity: "0", transform: "translateY(-5px)" })
          beatAt(280, () => {
            if (vals[j]) vals[j].textContent = row[0]
            if (deltas[j]) {
              deltas[j].innerHTML = '<span aria-hidden="true">' + row[1] + "</span>" + row[2]
              deltas[j].style.color = row[3]
            }
            if (notes[j]) notes[j].textContent = row[4]
            setAll(parts, { transition: "none", transform: "translateY(6px)" })
            requestAnimationFrame(() =>
              setAll(parts, { transition: "opacity .42s ease, transform .42s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }),
            )
          })
        }),
      )
    }

    const pulseTrend = (g: number) => {
      if (!revealed[0] || !trendLine) return
      const fill = root.querySelector<SVGPathElement>('[data-kf="trendfill"]')
      const dot = q('[data-kf="tdot"]')[0]
      const SHAPES = [
        { d: "M0 52 L80 44 L160 58 L240 40 L320 24 L400 30 L480 46 L560 62 L640 74", end: 74 },
        { d: "M0 38 L80 46 L160 36 L240 50 L320 42 L400 60 L480 70 L560 80 L640 88", end: 88 },
        { d: "M0 76 L80 70 L160 74 L240 62 L320 56 L400 44 L480 40 L560 30 L640 22", end: 22 },
      ]
      const sh = SHAPES[g % SHAPES.length]
      trendLine.setAttribute("d", sh.d)
      if (fill) fill.setAttribute("d", sh.d + " L640 96 L0 96 Z")
      const len = Math.ceil(trendLine.getTotalLength ? trendLine.getTotalLength() : trendLen)
      trendLen = len
      set(trendLine, { transition: "none", strokeDasharray: String(len), strokeDashoffset: String(len) })
      set(dot, { transition: "opacity .25s ease", opacity: "0" })
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          set(trendLine, { transition: "stroke-dashoffset 1.7s cubic-bezier(.35,0,.25,1)", strokeDashoffset: "0" }),
        ),
      )
      beatAt(1500, () =>
        set(dot, {
          transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.9,.3,1), top .5s cubic-bezier(.2,.7,.2,1)",
          top: ((sh.end / 96) * 100).toFixed(1) + "%",
          opacity: "1",
          transform: "none",
        }),
      )
    }

    const pulseBars = () => {
      if (!revealed[3]) return
      q('[data-kf="cbar"]').forEach((el, j) =>
        beatAt(j * 80, () => {
          set(el, { transition: "transform .45s cubic-bezier(.4,0,.6,1)", transform: "scaleX(0)" })
          beatAt(470, () => set(el, { transition: "transform 1s cubic-bezier(.2,.7,.2,1)", transform: "scaleX(1)" }))
        }),
      )
    }

    const focusRow = (i: number) => {
      if (!revealed[3]) return
      q('[data-kf="cfocus"]').forEach((el, j) => set(el, { transition: "opacity .6s ease", opacity: j === i ? "1" : "0" }))
    }

    const scanSources = (i: number) => {
      if (!revealed[4]) return
      q('[data-kf="srow"]').forEach((el, j) =>
        set(el, {
          transition: "opacity .6s ease, background .6s ease",
          opacity: j === i ? "1" : ".6",
          background: j === i ? "rgba(178,224,240,.05)" : "transparent",
        }),
      )
    }

    const stepEvidence = (i: number) => {
      if (!revealed[1]) return
      const hls = q('[data-kf="hl"]')
      hls.forEach((el, j) => {
        el.style.transition = "border-color .6s ease, background .6s ease, box-shadow .6s ease"
        if (j === i) {
          el.style.borderColor = "rgba(178,224,240,.5)"
          el.style.background = "rgba(178,224,240,.15)"
          el.style.boxShadow = "0 0 18px -5px rgba(178,224,240,.6)"
        } else {
          el.style.borderColor = ""
          el.style.background = ""
          el.style.boxShadow = ""
        }
      })
      q('[data-kf="cite"]').forEach((el, j) =>
        set(el, {
          transition: "border-color .5s ease, color .5s ease, background .5s ease",
          borderColor: j === i ? "rgba(178,224,240,.45)" : "rgba(255,255,255,.1)",
          background: j === i ? "rgba(178,224,240,.1)" : "transparent",
          color: j === i ? "#CFEEF7" : "rgba(232,238,244,.68)",
        }),
      )
      q('[data-kf="sup"]').forEach((el, j) => set(el, { transition: "opacity .5s ease", opacity: j === i ? "1" : ".45" }))
      q('[data-kf="cls"]').forEach((el, j) =>
        set(el, { transition: "background .6s ease", background: j === i ? "rgba(178,224,240,.07)" : "transparent" }),
      )
      // the position badge belongs to the tracked brand, so it lights on its mention
      const pos = q('[data-kf="pos"]')[0]
      const tracked = i === hls.length - 1
      set(pos, {
        borderColor: tracked ? "rgba(178,224,240,.45)" : "rgba(232,238,244,.16)",
        background: tracked ? "rgba(178,224,240,.12)" : "rgba(232,238,244,.05)",
        color: tracked ? "#CFEEF7" : "rgba(232,238,244,.68)",
      })
      // a soft pass over the captured answer, once per cycle
      if (i === 0) {
        const sw = q('[data-kf="sweep"]')[0]
        if (sw) {
          set(sw, { transition: "none", opacity: "0", transform: "translateY(-40px)" })
          requestAnimationFrame(() =>
            requestAnimationFrame(() =>
              set(sw, { transition: "transform 2.1s cubic-bezier(.4,0,.5,1), opacity .5s ease", opacity: "1", transform: "translateY(150px)" }),
            ),
          )
          beatAt(1900, () => set(sw, { transition: "opacity .5s ease", opacity: "0" }))
        }
      }
    }

    let lastG = -1
    const tick = () => {
      if (document.hidden) return
      clearBeat()
      beat = beat == null ? 0 : beat + 1
      const b = beat
      const held = Date.now() < holdUntilRef.current
      const g = held ? TABS.indexOf(heldTabRef.current) : b % 3
      if (!held) setTab(TABS[g])
      if (g !== lastG) {
        setMetrics(g)
        pulseTrend(g)
      }
      lastG = g
      focusRow(b % 4)
      if (g === 0 && !held) pulseBars()
      scanSources(b % 4)
      stepEvidence(b % 3)
    }

    const startLoop = (i: number) => {
      vis[i] = true
      if (clock) return
      clock = window.setInterval(tick, 3400)
    }
    const stopLoop = (i: number) => {
      vis[i] = false
      if (Object.keys(vis).some((k) => vis[Number(k)])) return
      if (clock) clearInterval(clock)
      clock = null
    }

    // ---- observers ------------------------------------------------------
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (!en.isIntersecting) return
          io.unobserve(en.target)
          reveal(en.target as HTMLElement)
        }),
      { threshold: 0.01, rootMargin: "0px 2400px -6% 2400px" },
    )
    q('[data-kf="hdr"]').forEach((el, i) => {
      el.dataset.kfDelay = String(i * 100)
      io.observe(el)
    })
    q('[data-kf="card"]').forEach((el, i) => {
      el.dataset.kfDelay = String((i % 2) * 120)
      io.observe(el)
    })
    observers.push(io)

    // ambient loops only run while their block is on screen
    const live = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          const i = Number((en.target as HTMLElement).dataset.kfIndex)
          en.isIntersecting ? startLoop(i) : stopLoop(i)
        }),
      { threshold: 0.01, rootMargin: "0px 2400px 0px 2400px" },
    )
    q('[data-kf="card"]').forEach((el) => live.observe(el))
    observers.push(live)

    // safety: nothing may stay hidden if an observer never fires
    at(2600, () =>
      q('[data-kf="hdr"], [data-kf="card"]').forEach((el) => {
        if (getComputedStyle(el).opacity !== "0") return
        reveal(el)
      }),
    )

    return () => {
      timers.forEach(clearTimeout)
      clearBeat()
      if (clock) clearInterval(clock)
      observers.forEach((o) => o.disconnect())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section id="features" ref={rootRef} aria-labelledby="kf-headline" className="velova-key-features section-default scroll-mt-24">
      <div aria-hidden="true" className="velova-kf-glow" />

      <div className="velova-container">

      {/* compact header */}
      <header className="velova-kf-header">
        <div>
          <div data-kf="hdr" className="velova-kf-eyebrow">
            <span aria-hidden="true" className="velova-kf-eyebrow-gem" />
            <span className="velova-kf-eyebrow-label">Inside Velova</span>
          </div>
          <h2 data-kf="hdr" id="kf-headline" className="velova-kf-headline">
            See the full picture.
          </h2>
          <p data-kf="hdr" className="velova-kf-editorial">
            Then trace it back to the answer.
          </p>
        </div>
        <p data-kf="hdr" className="velova-kf-lede">
          Track how your brand appears, see who is being recommended instead, find the sources
          shaping the result, and inspect what AI actually said.
        </p>
      </header>

      {/* one bento composition, five product moments */}
      <div className="velova-kf-bento">
        {/* PERFORMANCE */}
        <article data-kf="card" className="velova-kf-card velova-kf-card-perf">
          <span className="velova-kf-label">Performance</span>
          <h3 className="velova-kf-card-title">
            Know <em className="velova-kf-em">where your brand stands</em>.
          </h3>
          <p className="velova-kf-card-sub">
            See how your visibility, position and sentiment change across the questions and AI
            platforms that matter.
          </p>

          <div className="velova-kf-panel">
            <div className="velova-kf-filterbar">
              <span data-kf="fgroup" className="velova-kf-fgroup">
                Discovery questions
              </span>
              <span aria-hidden="true" className="velova-kf-vdiv" />
              <span data-kf="frange" className="velova-kf-frange">
                Jun 12 – Jul 11
              </span>
              <span aria-hidden="true" className="velova-kf-fspacer" />
              <span className="velova-kf-engines-note">
                <EngineStack />
                <span className="velova-kf-engines-label">4 platforms</span>
              </span>
              <span aria-hidden="true" className="velova-kf-vdiv" />
              <span className="velova-kf-runs">139 prompts · 278 runs</span>
            </div>

            <div className="velova-kf-metrics">
              <div data-kf="metric" className="velova-kf-metric">
                <span className="velova-kf-metric-label">Visibility score</span>
                <span className="velova-kf-metric-line">
                  <strong data-kf="mval" className="velova-kf-mval">
                    8.3%
                  </strong>
                  <span data-kf="mdelta" className="velova-kf-mdelta">
                    <span aria-hidden="true">▼</span>4.6
                  </span>
                </span>
                <span data-kf="mnote" className="velova-kf-mnote">
                  Needs attention
                </span>
              </div>
              <div data-kf="metric" className="velova-kf-metric">
                <span className="velova-kf-metric-label">Share of voice</span>
                <span className="velova-kf-metric-line">
                  <strong data-kf="mval" className="velova-kf-mval">
                    6.0%
                  </strong>
                  <span data-kf="mdelta" className="velova-kf-mdelta">
                    <span aria-hidden="true">▼</span>3.3
                  </span>
                </span>
                <span data-kf="mnote" className="velova-kf-mnote">
                  Low
                </span>
              </div>
              <div data-kf="metric" className="velova-kf-metric">
                <span className="velova-kf-metric-label">First choice rate</span>
                <span className="velova-kf-metric-line">
                  <strong data-kf="mval" className="velova-kf-mval">
                    3.6%
                  </strong>
                  <span data-kf="mdelta" className="velova-kf-mdelta velova-kf-mdelta-up">
                    <span aria-hidden="true">▲</span>1.1
                  </span>
                </span>
                <span data-kf="mnote" className="velova-kf-mnote">
                  Medium
                </span>
              </div>
            </div>

            <div className="velova-kf-trendwrap">
              <div className="velova-kf-trendhead">
                <span className="velova-kf-trendlabel">Daily trend · visibility</span>
                <span data-kf="bigmove" className="velova-kf-bigmove">
                  Biggest daily move ▼5.7%
                </span>
              </div>
              <span className="velova-kf-trendbox">
                <svg
                  viewBox="0 0 640 96"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  focusable="false"
                  className="velova-kf-trendsvg"
                >
                  <defs>
                    <linearGradient id="kfFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="rgba(178,224,240,.28)" />
                      <stop offset="1" stopColor="rgba(178,224,240,0)" />
                    </linearGradient>
                  </defs>
                  <rect data-kf="fband" x="516" y="0" width="124" height="96" fill="rgba(217,185,138,.055)" />
                  <line data-kf="fband" x1="516" y1="0" x2="516" y2="96" stroke="rgba(217,185,138,.3)" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
                  <line x1="0" y1="18" x2="640" y2="18" stroke="rgba(255,255,255,.05)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  <line x1="0" y1="56" x2="640" y2="56" stroke="rgba(255,255,255,.05)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  <path data-kf="trendfill" d="M0 52 L80 44 L160 58 L240 40 L320 24 L400 30 L480 46 L560 62 L640 74 L640 96 L0 96 Z" fill="url(#kfFill)" />
                  <path data-kf="trend" d="M0 52 L80 44 L160 58 L240 40 L320 24 L400 30 L480 46 L560 62 L640 74" fill="none" stroke="var(--kf-accent, #B2E0F0)" strokeWidth="1.75" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <span data-kf="tdot" aria-hidden="true" className="velova-kf-tdot" />
              </span>
            </div>
          </div>
        </article>

        {/* ANSWER EVIDENCE */}
        <article data-kf="card" className="velova-kf-card velova-kf-card-evidence">
          <span className="velova-kf-label">Answer evidence</span>
          <h3 className="velova-kf-card-title">
            Open the answer <em className="velova-kf-em">behind the result</em>.
          </h3>
          <p className="velova-kf-card-sub">
            Inspect the original question, captured response, brand mentions and citations behind
            each signal.
          </p>

          <div className="velova-kf-ev-panel">
            <div className="velova-kf-evq">
              <p className="velova-kf-evq-text">Which fitness tracker is best for sleep tracking?</p>
              <span className="velova-kf-evq-meta">Discovery intelligence · Jul 10</span>
            </div>

            <div className="velova-kf-evrow">
              <span aria-hidden="true" data-kf="pop" className="velova-kf-engine-md">
                <img src={ENGINE.chatgpt} alt="" />
              </span>
              <span className="velova-kf-evrow-engine">ChatGPT</span>
              <span data-kf="pos" className="velova-kf-pos">
                Position 3rd
              </span>
              <span aria-hidden="true" className="velova-kf-fspacer" />
              <span className="velova-kf-valid">Valid</span>
            </div>

            <div className="velova-kf-evbody">
              <span data-kf="sweep" aria-hidden="true" className="velova-kf-sweep" />
              <p className="velova-kf-evtext">
                <span data-kf="hl" className="velova-kf-hl">
                  Oura Ring 4
                </span>{" "}
                tracks sleep stages accurately in a ring you barely feel
                <sup data-kf="sup" className="velova-kf-sup">
                  1
                </sup>
                .{" "}
                <span data-kf="hl" className="velova-kf-hl">
                  Whoop 5.0
                </span>{" "}
                is screenless and focused on recovery and HRV
                <sup data-kf="sup" className="velova-kf-sup">
                  2
                </sup>
                , and{" "}
                <span data-kf="hl" className="velova-kf-hl velova-kf-hl-you">
                  Apple Watch
                </span>{" "}
                breaks sleep stages down well, but syncs slowly
                <sup data-kf="sup" className="velova-kf-sup">
                  3
                </sup>
                .
              </p>

              <div className="velova-kf-cites">
                <span className="velova-kf-cites-label">Citations</span>
                <span data-kf="cite" className="velova-kf-cite">
                  <span aria-hidden="true" className="velova-kf-cite-num">
                    1
                  </span>
                  ouraring.com
                </span>
                <span data-kf="cite" className="velova-kf-cite">
                  <span aria-hidden="true" className="velova-kf-cite-num">
                    2
                  </span>
                  whoop.com
                </span>
                <span data-kf="cite" className="velova-kf-cite">
                  <span aria-hidden="true" className="velova-kf-cite-num">
                    3
                  </span>
                  tomsguide.com
                </span>
              </div>
            </div>

            <div className="velova-kf-cls-grid">
              <div data-kf="cls" className="velova-kf-cls">
                <span className="velova-kf-cls-label">Presence</span>
                <strong className="velova-kf-cls-val">Secondary</strong>
              </div>
              <div data-kf="cls" className="velova-kf-cls">
                <span className="velova-kf-cls-label">Sentiment</span>
                <strong className="velova-kf-cls-val">Neutral</strong>
              </div>
              <div data-kf="cls" className="velova-kf-cls">
                <span className="velova-kf-cls-label">Outcome</span>
                <strong className="velova-kf-cls-val">Loss</strong>
              </div>
            </div>
          </div>
        </article>

        {/* QUESTIONS */}
        <article data-kf="card" className="velova-kf-card velova-kf-card-third">
          <span className="velova-kf-label">Questions</span>
          <h3 className="velova-kf-card-title">
            Track the questions <em className="velova-kf-em">buyers are asking</em>.
          </h3>
          <p className="velova-kf-card-sub">
            Follow discovery, comparison and trust questions—and see where your brand appears or
            disappears.
          </p>

          <div className="velova-kf-panel">
            <div className="velova-kf-tabs" role="group" aria-label="Question groups">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => selectTab(t)}
                  aria-pressed={tab === t}
                  className={`velova-kf-tab${tab === t ? " velova-kf-tab-on" : ""}`}
                >
                  {t === "discovery" ? "Discovery" : t === "comparison" ? "Comparison" : "Trust"}
                </button>
              ))}
            </div>

            <div ref={qpanelRef} id="kf-q-panel" role="group" aria-label="Buyer questions Velova tracks" className="velova-kf-qpanel">
              {tab === "discovery" && (
                <>
                  <div className="velova-kf-qrow">
                    <span className="velova-kf-qtext">Which fitness tracker is best for sleep tracking?</span>
                    <span className="velova-kf-qscore">
                      45<span className="velova-kf-qscore-denom">/100</span>
                    </span>
                    <span className="velova-kf-qeng">
                      <span aria-hidden="true" className="velova-kf-engine-sm">
                        <img src={ENGINE.chatgpt} alt="" />
                      </span>
                      <span className="velova-kf-qmark">✓</span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow velova-kf-qrow-focus">
                    <span className="velova-kf-qtext">Best fitness tracker for beginners?</span>
                    <span className="velova-kf-qscore velova-kf-qscore-warn">
                      0<span className="velova-kf-qscore-denom">/100</span>
                    </span>
                    <span className="velova-kf-qeng">
                      <span aria-hidden="true" className="velova-kf-engine-sm">
                        <img src={ENGINE.chatgpt} alt="" />
                      </span>
                      <span className="velova-kf-qmark velova-kf-qmark-none">—</span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow">
                    <span className="velova-kf-qtext">Best tracker with long battery life?</span>
                    <span className="velova-kf-qscore">
                      62<span className="velova-kf-qscore-denom">/100</span>
                    </span>
                    <span className="velova-kf-qeng">
                      <span aria-hidden="true" className="velova-kf-engine-sm">
                        <img src={ENGINE.gemini} alt="" />
                      </span>
                      <span className="velova-kf-qmark">✓</span>
                    </span>
                  </div>
                </>
              )}

              {tab === "comparison" && (
                <>
                  <div className="velova-kf-qrow velova-kf-qrow-cmp">
                    <span className="velova-kf-qtext">Apple Watch vs Oura Ring 4 for sleep</span>
                    <span className="velova-kf-vbadge velova-kf-vbadge-loss">Loss</span>
                    <span className="velova-kf-meter">
                      <span aria-hidden="true" className="velova-kf-meter-track">
                        <span className="velova-kf-meter-fill" style={{ width: "25%", background: "rgba(217,185,138,.75)" }} />
                      </span>
                      <span className="velova-kf-meter-val">25%</span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow velova-kf-qrow-cmp velova-kf-qrow-focus">
                    <span className="velova-kf-qtext">Compare Apple Watch and Whoop 5.0</span>
                    <span className="velova-kf-vbadge velova-kf-vbadge-win">Win</span>
                    <span className="velova-kf-meter">
                      <span aria-hidden="true" className="velova-kf-meter-track">
                        <span className="velova-kf-meter-fill" style={{ width: "100%", background: "rgba(143,217,196,.8)" }} />
                      </span>
                      <span className="velova-kf-meter-val" style={{ color: "#E8F3EF" }}>
                        100%
                      </span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow velova-kf-qrow-cmp">
                    <span className="velova-kf-qtext">Apple Watch or Fitbit Inspire 3</span>
                    <span className="velova-kf-vbadge">Tie</span>
                    <span className="velova-kf-meter">
                      <span aria-hidden="true" className="velova-kf-meter-track">
                        <span className="velova-kf-meter-fill" style={{ width: "50%", background: "rgba(170,206,228,.6)" }} />
                      </span>
                      <span className="velova-kf-meter-val">50%</span>
                    </span>
                  </div>
                </>
              )}

              {tab === "trust" && (
                <>
                  <div className="velova-kf-qrow velova-kf-qrow-trust">
                    <span className="velova-kf-qtext">Is Apple Watch sleep tracking accurate?</span>
                    <span className="velova-kf-tmeta">
                      <span className="velova-kf-vbadge velova-kf-vbadge-win">Positive</span>
                      <span className="velova-kf-tsrc">apple.com · 6/8</span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow velova-kf-qrow-trust velova-kf-qrow-focus">
                    <span className="velova-kf-qtext">Is Apple Watch worth it for sleep?</span>
                    <span className="velova-kf-tmeta">
                      <span className="velova-kf-vbadge">Neutral</span>
                      <span className="velova-kf-tsrc">tomsguide.com · 4/7</span>
                    </span>
                  </div>
                  <div className="velova-kf-qrow velova-kf-qrow-trust">
                    <span className="velova-kf-qtext">Does Apple Watch battery last a night?</span>
                    <span className="velova-kf-tmeta">
                      <span className="velova-kf-vbadge">Neutral</span>
                      <span className="velova-kf-tsrc">reddit.com · 3/5</span>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </article>

        {/* COMPETITORS */}
        <article data-kf="card" className="velova-kf-card velova-kf-card-third">
          <span className="velova-kf-label">Competitors</span>
          <h3 className="velova-kf-card-title">
            See who AI <em className="velova-kf-em">puts ahead of you</em>.
          </h3>
          <p className="velova-kf-card-sub">
            Compare how often brands appear, who gets recommended first and where competitors are
            gaining ground.
          </p>

          <div className="velova-kf-panel">
            <div className="velova-kf-chead">
              <span className="velova-kf-thlabel">Brand</span>
              <span className="velova-kf-thlabel velova-kf-thlabel-r">Share</span>
              <span className="velova-kf-thlabel velova-kf-thlabel-r">1st</span>
            </div>

            <div data-kf="crow" className="velova-kf-crow">
              <span data-kf="cfocus" aria-hidden="true" className="velova-kf-cfocus" />
              <span data-kf="cbar" aria-hidden="true" className="velova-kf-cbar" style={{ width: "100%" }} />
              <span className="velova-kf-cname">
                <span aria-hidden="true" className="velova-kf-crank">
                  1
                </span>
                <BrandMark src={BRAND.oura} />
                <span className="velova-kf-cbrand">Oura Ring 4</span>
              </span>
              <span className="velova-kf-cshare">21.4%</span>
              <span className="velova-kf-cfirst">61%</span>
            </div>
            <div data-kf="crow" className="velova-kf-crow">
              <span data-kf="cfocus" aria-hidden="true" className="velova-kf-cfocus" />
              <span data-kf="cbar" aria-hidden="true" className="velova-kf-cbar" style={{ width: "78%" }} />
              <span className="velova-kf-cname">
                <span aria-hidden="true" className="velova-kf-crank">
                  2
                </span>
                <BrandMark src={BRAND.whoop} />
                <span className="velova-kf-cbrand">Whoop 5.0</span>
              </span>
              <span className="velova-kf-cshare">16.8%</span>
              <span className="velova-kf-cfirst">24%</span>
            </div>
            <div data-kf="crow" className="velova-kf-crow">
              <span data-kf="cfocus" aria-hidden="true" className="velova-kf-cfocus" />
              <span data-kf="cbar" aria-hidden="true" className="velova-kf-cbar" style={{ width: "57%" }} />
              <span className="velova-kf-cname">
                <span aria-hidden="true" className="velova-kf-crank">
                  3
                </span>
                <BrandMark src={BRAND.fitbit} />
                <span className="velova-kf-cbrand">Fitbit Inspire 3</span>
              </span>
              <span className="velova-kf-cshare">12.3%</span>
              <span className="velova-kf-cfirst">11%</span>
            </div>
            <div data-kf="crow" className="velova-kf-crow velova-kf-crow-you">
              <span data-kf="cfocus" aria-hidden="true" className="velova-kf-cfocus" />
              <span data-kf="cbar" aria-hidden="true" className="velova-kf-cbar" style={{ width: "38%" }} />
              <span className="velova-kf-cname">
                <span aria-hidden="true" className="velova-kf-crank">
                  4
                </span>
                <BrandMark src={BRAND.apple} />
                <span className="velova-kf-cbrand">Apple Watch</span>
                <span className="velova-kf-you-chip">You</span>
              </span>
              <span className="velova-kf-cshare">8.1%</span>
              <span className="velova-kf-cfirst">7%</span>
            </div>

            <div data-kf="foot" className="velova-kf-cfoot">
              <span className="velova-kf-engines-note">
                <EngineStack />
                <span className="velova-kf-engines-label">All engines</span>
              </span>
              <span aria-hidden="true" className="velova-kf-cfoot-spacer" />
              <span className="velova-kf-gaplabel">Gap</span>
              <span className="velova-kf-gapval">4.6 pts</span>
            </div>
          </div>
        </article>

        {/* SOURCES */}
        <article data-kf="card" className="velova-kf-card velova-kf-card-third">
          <span className="velova-kf-label">Sources</span>
          <h3 className="velova-kf-card-title">
            Find what is <em className="velova-kf-em">shaping the answer</em>.
          </h3>
          <p className="velova-kf-card-sub">
            See which pages and domains support your brand, support competitors, or leave an
            important gap.
          </p>

          <div className="velova-kf-panel">
            <div className="velova-kf-shead">
              <span className="velova-kf-thlabel">Domain</span>
              <span className="velova-kf-thlabel velova-kf-thlabel-r">Power</span>
              <span className="velova-kf-thlabel velova-kf-thlabel-r">Presence</span>
            </div>
            <div data-kf="srow" className="velova-kf-srow">
              <span className="velova-kf-sdomain">
                <BrandMark src={BRAND.apple} />
                <span className="velova-kf-sname">apple.com</span>
                <span className="velova-kf-stag velova-kf-stag-owned">Owned</span>
              </span>
              <span className="velova-kf-spower">51.8</span>
              <span className="velova-kf-spresence">
                <span aria-hidden="true" className="velova-kf-pdot" />
                Present
              </span>
            </div>
            <div data-kf="srow" className="velova-kf-srow">
              <span className="velova-kf-sdomain">
                <BrandMark src={BRAND.tomsguide} />
                <span className="velova-kf-sname">tomsguide.com</span>
                <span className="velova-kf-stag">3rd party</span>
              </span>
              <span className="velova-kf-spower">48.2</span>
              <span className="velova-kf-spresence">
                <span aria-hidden="true" className="velova-kf-pdot" />
                Present
              </span>
            </div>
            <div data-kf="srow" className="velova-kf-srow">
              <span className="velova-kf-sdomain">
                <BrandMark src={BRAND.oura} />
                <span className="velova-kf-sname">ouraring.com</span>
                <span className="velova-kf-stag">Competitor</span>
              </span>
              <span className="velova-kf-spower">47.6</span>
              <span className="velova-kf-spresence">
                <span aria-hidden="true" className="velova-kf-pdot velova-kf-pdot-miss" />
                Missing
              </span>
            </div>
            <div data-kf="srow" className="velova-kf-srow velova-kf-srow-gap">
              <span className="velova-kf-sdomain">
                <BrandMark src={BRAND.reddit} />
                <span className="velova-kf-sname">reddit.com</span>
                <span className="velova-kf-stag velova-kf-stag-gap">Gap</span>
              </span>
              <span className="velova-kf-spower">46.9</span>
              <span className="velova-kf-spresence">
                <span aria-hidden="true" className="velova-kf-pdot velova-kf-pdot-miss" />
                Missing
              </span>
            </div>
            <div data-kf="foot" className="velova-kf-sfoot">
              <span className="velova-kf-sfoot-label">Gap on</span>
              <span className="velova-kf-sfoot-text">Best sleep tracking question · cited in 34 answers</span>
            </div>
          </div>
        </article>
      </div>

      {/* bridge into the Actions section — revealed like the header lines */}
      <div data-kf="hdr" className="velova-kf-bridge">
        <span aria-hidden="true" className="velova-kf-bridge-rule" />
        <p className="velova-kf-bridge-a">Seeing the pattern is only the beginning.</p>
        <p className="velova-kf-bridge-b">Next, Velova shows you what to do.</p>
      </div>
      </div>
    </section>
  )
}
